import React, { useState, useEffect, useMemo } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import Papa from "papaparse";
import { ClipLoader } from "react-spinners";
import {
  FaUndo,
  FaCloudUploadAlt,
  FaBroom,
  FaCalculator,
  FaFileCsv,
  FaFileCode,
  FaObjectGroup,
  FaEraser,
  FaTable,
  FaFilter,
  FaHashtag,
  FaFont,
  FaBars,
  FaTimes,
  FaLightbulb,
  FaMagic,
  FaArrowRight,
  FaLink,
  FaPlus,
  FaEquals,
  FaRandom,
  FaLayerGroup,
  FaSave,
  FaColumns,
} from "react-icons/fa";

// --- FIREBASE IMPORTS ---
import { ref, uploadBytesResumable, getDownloadURL } from "firebase/storage";
import { doc, setDoc, collection, serverTimestamp } from "firebase/firestore";
import { db, storage, auth } from "../../backend/firebase";

import "./CSVCanvas.css";

// --- CONSTANTS ---
const ENCODING_FILE_ID = "system_encoding_json";
const ENCODING_FILE_NAME = "encoding_map.json";

const CSVCanvas = () => {
  const { state } = useLocation();
  const navigate = useNavigate();
  const { datasetid, versionid } = useParams();

  // --- STATE MANAGEMENT ---
  const [filesList, setFilesList] = useState([]);
  const [activeFileId, setActiveFileId] = useState(null);
  const [fileStates, setFileStates] = useState({});

  // UI State
  const [isSidebarOpen, setIsSidebarOpen] = useState(true);
  const [activeTool, setActiveTool] = useState("structure");
  const [selectedCol, setSelectedCol] = useState("");
  const [statusMsg, setStatusMsg] = useState("");
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [suggestions, setSuggestions] = useState([]);

  // PROCESSING STATE
  const [processingAction, setProcessingAction] = useState(null);

  // Input States
  const [renameValue, setRenameValue] = useState("");
  const [customFillValue, setCustomFillValue] = useState("");

  // Merge State
  const [mergeConfig, setMergeConfig] = useState({
    targetFileId: "",
    primaryKey: "",
    secondaryKey: "",
    joinType: "left",
  });

  // Save State
  const [showSaveModal, setShowSaveModal] = useState(false);
  const [filesToSave, setFilesToSave] = useState({});
  const [isSaving, setIsSaving] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);

  // --- INITIALIZATION ---
  useEffect(() => {
    if (state?.files && state.files.length > 0) {
      setFilesList(state.files);
      setActiveFileId(state.files[0].id);

      const initialSaveState = {};
      state.files.forEach((f) => {
        initialSaveState[f.id] = {
          selected: true,
          name: f.name.replace(".csv", ""),
          isMandatory: f.isSystemFile || false,
        };
        if (f.type !== "json_mapping") {
          loadFile(f.id, f.url || f.file, f.name);
        }
      });
      setFilesToSave(initialSaveState);
    }
    if (window.innerWidth < 768) setIsSidebarOpen(false);
  }, [state, navigate]);

  // Sync Save List
  useEffect(() => {
    setFilesToSave((prev) => {
      const newState = { ...prev };
      filesList.forEach((f) => {
        if (!newState[f.id]) {
          newState[f.id] = {
            selected: true,
            name: f.name.replace(/\.(csv|json)$/, ""),
            isMandatory: f.isSystemFile || false,
          };
        }
      });
      return newState;
    });
  }, [filesList]);

  // Clean up UI when switching files
  useEffect(() => {
    setSelectedCol("");
    setStatusMsg("");
    setRenameValue("");
    setShowSuggestions(false);
  }, [activeFileId]);

  // --- AI SUGGESTION ENGINE ---
  useEffect(() => {
    if (!activeFileId || !fileStates[activeFileId]?.data) return;
    const current = fileStates[activeFileId];
    if (current.type === "json_mapping" || !Array.isArray(current.data)) return;

    const newSuggestions = [];
    const data = current.data;
    const headers = current.headers;
    const rowCount = data.length;

    headers.forEach((col) => {
      const values = data.map((r) => r[col]);
      const nonNulls = values.filter(
        (v) => v !== null && v !== "" && v !== undefined
      );
      const missingCount = rowCount - nonNulls.length;
      const missingPct = missingCount / rowCount;
      const isNum =
        nonNulls.length > 0 &&
        nonNulls.every((v) => !isNaN(parseFloat(v)) && isFinite(v));

      if (missingCount > 0) {
        newSuggestions.push({
          type: "missing",
          col: col,
          score: missingPct,
          title: `Missing: ${col}`,
          desc: `${missingCount} rows are empty (${(missingPct * 100).toFixed(
            1
          )}%).`,
          actionLabel: isNum ? "Impute Mean" : "Impute Mode",
          action: () => {
            setSelectedCol(col);
            setActiveTool("clean");
            fillMissing(isNum ? "mean" : "mode", col);
          },
        });
      }

      if (isNum) {
        const numericValues = nonNulls.map((v) => parseFloat(v));
        const min = Math.min(...numericValues);
        const max = Math.max(...numericValues);
        const variance = max - min;
        if (variance > 1000) {
          newSuggestions.push({
            type: "scale",
            col: col,
            score: 0.5,
            title: `Scale: ${col}`,
            desc: `Range: ${min} to ${max}. Scaling suggested.`,
            actionLabel: "Min-Max Scale",
            action: () => {
              setSelectedCol(col);
              setActiveTool("transform");
              handleNormalization("minmax", col);
            },
          });
        }
      }
    });
    setSuggestions(newSuggestions.sort((a, b) => b.score - a.score));
  }, [fileStates, activeFileId]);

  // --- CORE UTILS ---
  const getCurrentState = () =>
    activeFileId ? fileStates[activeFileId] : null;
  const isNumeric = (n) => !isNaN(parseFloat(n)) && isFinite(n);

  const getColType = useMemo(() => {
    const current = getCurrentState();
    if (!current || !selectedCol) return null;
    if (!Array.isArray(current.data)) return "string";
    const sample = current.data
      .slice(0, 100)
      .find((r) => r[selectedCol] !== null && r[selectedCol] !== "");
    if (!sample) return "string";
    return isNumeric(sample[selectedCol]) ? "number" : "string";
  }, [activeFileId, selectedCol, fileStates]);

  const updateCurrentFile = (newData, newHeaders = null, msg = "Updated") => {
    setFileStates((prev) => {
      const current = prev[activeFileId];
      const newHistory = [
        ...(current.history || []).slice(-9),
        { data: current.data, headers: current.headers },
      ];
      return {
        ...prev,
        [activeFileId]: {
          ...current,
          data: newData,
          headers: newHeaders || current.headers,
          history: newHistory,
        },
      };
    });
    setStatusMsg(msg);
    setTimeout(() => setStatusMsg(""), 3000);
  };

  const loadFile = async (fileId, source, fileName) => {
    setFileStates((prev) => {
      if (prev[fileId]?.loading || prev[fileId]?.data) return prev;
      return {
        ...prev,
        [fileId]: {
          ...prev[fileId],
          loading: true,
          error: null,
          name: fileName,
        },
      };
    });

    const processCSV = (csvText) => {
      Papa.parse(csvText, {
        header: true,
        skipEmptyLines: true,
        dynamicTyping: true,
        complete: (results) => {
          if (!results.data || !results.data.length) {
            setFileStates((prev) => ({
              ...prev,
              [fileId]: { loading: false, error: "Empty File", name: fileName },
            }));
            return;
          }
          const rawData = results.data;
          const headers = results.meta.fields || Object.keys(rawData[0]);
          setFileStates((prev) => ({
            ...prev,
            [fileId]: {
              data: rawData,
              headers: headers,
              name: fileName,
              metrics: {
                totalRows: rawData.length,
                totalColumns: headers.length,
              },
              history: [],
              loading: false,
              type: "csv",
            },
          }));
        },
        error: (err) =>
          setFileStates((prev) => ({
            ...prev,
            [fileId]: { loading: false, error: "Error", name: fileName },
          })),
      });
    };

    try {
      if (source instanceof File) {
        const reader = new FileReader();
        reader.onload = (e) => processCSV(e.target.result);
        reader.readAsText(source);
      } else if (typeof source === "string") {
        const finalUrl =
          "https://calm-charm-production.up.railway.app/proxy?url=" +
          encodeURIComponent(source);
        const response = await fetch(finalUrl);
        const text = await response.text();
        processCSV(text);
      }
    } catch (error) {
      setFileStates((prev) => ({
        ...prev,
        [fileId]: { loading: false, error: "Failed", name: fileName },
      }));
    }
  };

  const runAction = (actionId, logicFn) => {
    setProcessingAction(actionId);
    setTimeout(() => {
      try {
        logicFn();
      } catch (e) {
        alert("Error: " + e.message);
      } finally {
        setProcessingAction(null);
      }
    }, 50);
  };

  // --- BUSINESS LOGIC (Same as original) ---
  const handleDropColumn = (overrideCol = null) => {
    const targetCol = overrideCol || selectedCol;
    if (!targetCol) return alert("Select column");
    if (!window.confirm(`Drop "${targetCol}"?`)) return;
    runAction("drop_col", () => {
      const current = getCurrentState();
      const newHeaders = current.headers.filter((h) => h !== targetCol);
      const newData = current.data.map((row) => {
        const r = { ...row };
        delete r[targetCol];
        return r;
      });
      updateCurrentFile(newData, newHeaders, `Dropped ${targetCol}`);
      if (!overrideCol) setSelectedCol("");
    });
  };

  const handleRenameColumn = () => {
    if (!selectedCol || !renameValue.trim()) return alert("Enter name");
    runAction("rename_col", () => {
      const current = getCurrentState();
      const newHeaders = current.headers.map((h) =>
        h === selectedCol ? renameValue : h
      );
      const newData = current.data.map((row) => {
        const r = {};
        Object.keys(row).forEach(
          (k) => (r[k === selectedCol ? renameValue : k] = row[k])
        );
        return r;
      });
      updateCurrentFile(newData, newHeaders, `Renamed to ${renameValue}`);
      setSelectedCol(renameValue);
      setRenameValue("");
    });
  };

  const handleTypeConversion = (targetType) => {
    if (!selectedCol) return alert("Select column");
    runAction(`convert_${targetType}`, () => {
      const current = getCurrentState();
      const newData = JSON.parse(JSON.stringify(current.data));
      newData.forEach((row) => {
        if (row[selectedCol] != null) {
          if (targetType === "number") {
            const p = parseFloat(row[selectedCol]);
            row[selectedCol] = isNaN(p) ? null : p;
          } else {
            row[selectedCol] = String(row[selectedCol]);
          }
        }
      });
      updateCurrentFile(newData, null, `Converted to ${targetType}.`);
    });
  };

  const handleRemoveDuplicates = () => {
    runAction("remove_dupe", () => {
      const current = getCurrentState();
      const uniqueSet = new Set();
      const newData = [];
      current.data.forEach((row) => {
        const str = JSON.stringify(row);
        if (!uniqueSet.has(str)) {
          uniqueSet.add(str);
          newData.push(row);
        }
      });
      updateCurrentFile(
        newData,
        null,
        `Removed ${current.data.length - newData.length} duplicates`
      );
    });
  };

  const handleTextCleaning = (action) => {
    if (!selectedCol) return alert("Select column");
    runAction(`clean_${action}`, () => {
      const current = getCurrentState();
      const newData = JSON.parse(JSON.stringify(current.data));
      newData.forEach((row) => {
        let val = row[selectedCol];
        if (typeof val === "string") {
          if (action === "trim") val = val.trim();
          if (action === "lower") val = val.toLowerCase();
          if (action === "upper") val = val.toUpperCase();
          if (action === "special") val = val.replace(/[^a-zA-Z0-9 ]/g, "");
          row[selectedCol] = val;
        }
      });
      updateCurrentFile(newData, null, `Applied ${action}`);
    });
  };

  const fillMissing = (method, overrideCol = null) => {
    const targetCol = overrideCol || selectedCol;
    if (!targetCol) return alert("Select column");
    runAction(`fill_${method}`, () => {
      const current = getCurrentState();
      let newData = JSON.parse(JSON.stringify(current.data));
      const validValues = newData
        .map((r) => r[targetCol])
        .filter((v) => v != null && v !== "");

      if (method === "drop") {
        newData = newData.filter(
          (r) => r[targetCol] != null && r[targetCol] !== ""
        );
        updateCurrentFile(newData, null, "Dropped Rows");
        return;
      }

      let fillValue;
      if (method === "mean")
        fillValue =
          validValues.reduce((a, b) => a + parseFloat(b), 0) /
          validValues.length;
      else if (method === "mode") {
        const c = {};
        validValues.forEach((v) => (c[v] = (c[v] || 0) + 1));
        fillValue = Object.keys(c).reduce((a, b) => (c[a] > c[b] ? a : b));
        if (!isNaN(parseFloat(fillValue))) fillValue = parseFloat(fillValue);
      } else if (method === "custom") fillValue = customFillValue;

      if (method === "mean" && !isNaN(fillValue))
        fillValue = parseFloat(fillValue.toFixed(4));

      newData.forEach((row) => {
        if (row[targetCol] == null || row[targetCol] === "")
          row[targetCol] = fillValue;
      });
      updateCurrentFile(newData, null, `Filled ${targetCol}`);
    });
  };

  const handleOutlierRemoval = () => {
    if (!selectedCol) return alert("Select numeric column");
    runAction("remove_outlier", () => {
      const current = getCurrentState();
      const values = current.data
        .map((r) => r[selectedCol])
        .filter((v) => isNumeric(v))
        .map((v) => parseFloat(v))
        .sort((a, b) => a - b);
      if (values.length < 4) throw new Error("Not enough data");
      const q1 = values[Math.floor(values.length / 4)];
      const q3 = values[Math.floor(values.length * (3 / 4))];
      const iqr = q3 - q1;
      const newData = current.data.filter((row) => {
        const v = row[selectedCol];
        return !isNumeric(v) || (v >= q1 - 1.5 * iqr && v <= q3 + 1.5 * iqr);
      });
      updateCurrentFile(
        newData,
        null,
        `Removed ${current.data.length - newData.length} outliers`
      );
    });
  };

  const handleNormalization = (type, overrideCol = null) => {
    const targetCol = overrideCol || selectedCol;
    if (!targetCol) return alert("Select column");
    runAction(`norm_${type}`, () => {
      const current = getCurrentState();
      const newData = JSON.parse(JSON.stringify(current.data));
      const values = newData
        .map((r) => r[targetCol])
        .filter((v) => isNumeric(v))
        .map((v) => parseFloat(v));
      if (!values.length) throw new Error("No numeric data");
      const min = Math.min(...values),
        max = Math.max(...values);
      const mean = values.reduce((a, b) => a + b, 0) / values.length;
      const std = Math.sqrt(
        values.map((x) => (x - mean) ** 2).reduce((a, b) => a + b) /
          values.length
      );

      newData.forEach((row) => {
        if (isNumeric(row[targetCol])) {
          let v = parseFloat(row[targetCol]);
          if (type === "log" && v > 0) v = Math.log(v);
          else if (type === "minmax" && max !== min)
            v = (v - min) / (max - min);
          else if (type === "zscore" && std !== 0) v = (v - mean) / std;
          row[targetCol] = parseFloat(v.toFixed(4));
        }
      });
      updateCurrentFile(newData, null, `${type} Applied`);
    });
  };

  const handleOneHotEncode = () => {
    if (!selectedCol) return alert("Select column");
    runAction("one_hot", () => {
      const current = getCurrentState();
      const uniqueValues = new Set(
        current.data
          .map((r) => r[selectedCol])
          .filter((v) => v != null && v !== "")
      );
      if (
        uniqueValues.size > 20 &&
        !window.confirm(`Create ${uniqueValues.size} columns?`)
      )
        return;
      let newData = JSON.parse(JSON.stringify(current.data));
      const uniques = Array.from(uniqueValues);
      const newCols = uniques.map((u) => `${selectedCol}_${u}`);
      const updatedHeaders = [
        ...current.headers.filter((h) => h !== selectedCol),
        ...newCols,
      ];
      newData = newData.map((row) => {
        const val = row[selectedCol];
        const newRow = { ...row };
        delete newRow[selectedCol];
        uniques.forEach((u) => {
          newRow[`${selectedCol}_${u}`] = val === u ? 1 : 0;
        });
        return newRow;
      });
      updateCurrentFile(newData, updatedHeaders, `One-Hot Encoded`);
      setSelectedCol("");
    });
  };

  const labelEncode = (overrideCol = null) => {
    const targetCol = overrideCol || selectedCol;
    if (!targetCol) return alert("Select column");
    runAction("label_enc", () => {
      const current = getCurrentState();
      let newData = JSON.parse(JSON.stringify(current.data));
      const uniques = [
        ...new Set(
          newData.map((r) => r[targetCol]).filter((v) => v != null && v !== "")
        ),
      ].sort();
      const map = {};
      uniques.forEach((v, i) => (map[v] = i));
      newData.forEach((row) => {
        if (map.hasOwnProperty(row[targetCol]))
          row[targetCol] = map[row[targetCol]];
      });

      const hasMap = filesList.some((f) => f.id === ENCODING_FILE_ID);
      let combinedMap =
        hasMap && fileStates[ENCODING_FILE_ID]
          ? { ...fileStates[ENCODING_FILE_ID].data }
          : {};
      combinedMap[targetCol] = map;
      if (!hasMap) {
        setFilesList((prev) => [
          ...prev,
          {
            id: ENCODING_FILE_ID,
            name: ENCODING_FILE_NAME,
            type: "json_mapping",
            isSystemFile: true,
          },
        ]);
      }
      setFileStates((prev) => ({
        ...prev,
        [ENCODING_FILE_ID]: {
          data: combinedMap,
          headers: ["Key", "Value"],
          loading: false,
          type: "json_mapping",
        },
      }));
      updateCurrentFile(newData, null, `Label Encoded ${targetCol}`);
    });
  };

  const performMerge = () => {
    const { targetFileId, primaryKey, secondaryKey, joinType } = mergeConfig;
    if (!targetFileId || !primaryKey || !secondaryKey)
      return alert("Please complete the merge configuration.");

    runAction("merge", () => {
      const fileA = fileStates[activeFileId];
      const fileB = fileStates[targetFileId];

      if (!fileB || !fileB.data)
        return alert("Target file is not ready. Please wait.");

      const bMap = new Map();
      fileB.data.forEach((row) => {
        const k = String(row[secondaryKey]);
        if (!bMap.has(k)) bMap.set(k, []);
        bMap.get(k).push(row);
      });

      let resultData = [];
      const usedBKeys = new Set();
      const prefix = (fileB.name || "FileB").substring(0, 3) + "_";

      fileA.data.forEach((rowA) => {
        const key = String(rowA[primaryKey]);
        const matchesB = bMap.get(key);

        if (matchesB) {
          usedBKeys.add(key);
          matchesB.forEach((rowB) => {
            const mergedRow = { ...rowA };
            Object.keys(rowB).forEach((k) => {
              if (k !== secondaryKey) mergedRow[`${prefix}${k}`] = rowB[k];
            });
            resultData.push(mergedRow);
          });
        } else if (joinType === "left" || joinType === "outer") {
          resultData.push(rowA);
        }
      });

      if (joinType === "right" || joinType === "outer") {
        bMap.forEach((rows, key) => {
          if (!usedBKeys.has(key)) {
            rows.forEach((rowB) => {
              const mergedRow = {};
              Object.keys(rowB).forEach((k) => {
                if (k !== secondaryKey) mergedRow[`${prefix}${k}`] = rowB[k];
                else mergedRow[primaryKey] = rowB[k];
              });
              resultData.push(mergedRow);
            });
          }
        });
      }

      const newId = `merged_${Date.now()}`;
      const newName = `Merged_Data_${Object.keys(filesList).length}.csv`;
      const newHeaders = Object.keys(resultData[0] || {});

      setFilesList((prev) => [
        ...prev,
        { id: newId, name: newName, type: "csv", isSystemFile: false },
      ]);
      setFileStates((prev) => ({
        ...prev,
        [newId]: {
          data: resultData,
          headers: newHeaders,
          metrics: {
            totalRows: resultData.length,
            totalColumns: newHeaders.length,
          },
          history: [],
          name: newName,
          loading: false,
          type: "csv",
        },
      }));

      setActiveFileId(newId);
      setActiveTool("structure");
      setMergeConfig({
        targetFileId: "",
        primaryKey: "",
        secondaryKey: "",
        joinType: "left",
      });
      setStatusMsg("Merge Complete!");
    });
  };

  const handleSaveInstance = async () => {
    if (!auth.currentUser) return alert("Login required");
    const selectedIds = Object.keys(filesToSave).filter(
      (id) => filesToSave[id].selected
    );
    if (!selectedIds.length) return alert("Select files");
    setIsSaving(true);
    setUploadProgress(0);
    const uploadedFiles = [];
    let completed = 0;
    try {
      const instancesRef = collection(
        db,
        "datasets",
        datasetid,
        "versions",
        versionid,
        "instances"
      );
      const newInstanceDoc = doc(instancesRef);
      const instanceId = newInstanceDoc.id;
      for (const id of selectedIds) {
        const fileState = fileStates[id];
        if (!fileState?.data) continue;
        const isJson =
          filesList.find((f) => f.id === id)?.type === "json_mapping";
        let blob, finalName;
        if (isJson) {
          blob = new Blob([JSON.stringify(fileState.data, null, 2)], {
            type: "application/json",
          });
          finalName = ENCODING_FILE_NAME;
        } else {
          blob = new Blob([Papa.unparse(fileState.data)], {
            type: "text/csv;charset=utf-8;",
          });
          finalName = `${filesToSave[id].name.replace(
            /\.(json|csv)$/,
            ""
          )}.csv`;
        }
        const path = `datasets/${datasetid}/${versionid}/instances/${instanceId}/${finalName}`;
        const storageRef = ref(storage, path);
        const url = await new Promise((resolve, reject) => {
          const task = uploadBytesResumable(storageRef, blob);
          task.on("state_changed", null, reject, async () =>
            resolve(await getDownloadURL(task.snapshot.ref))
          );
        });
        uploadedFiles.push({
          fileId: id,
          fileName: finalName,
          fileUrl: url,
          rowCount: isJson
            ? Object.keys(fileState.data).length
            : fileState.data.length,
          fileType: isJson ? "mapping" : "data",
        });
        completed++;
        setUploadProgress(Math.round((completed / selectedIds.length) * 100));
      }
      await setDoc(newInstanceDoc, {
        files: uploadedFiles,
        fileCount: uploadedFiles.length,
        savedAt: serverTimestamp(),
        lastModifiedBy: auth.currentUser.uid,
        instanceId: instanceId,
      });
      setStatusMsg("Instance Saved!");
      setTimeout(() => {
        setShowSaveModal(false);
        setIsSaving(false);
      }, 1500);
    } catch (e) {
      alert("Save failed: " + e.message);
      setIsSaving(false);
    }
  };

  const handleUndo = () => {
    const current = getCurrentState();
    if (!current?.history?.length) return;
    const previous = current.history[current.history.length - 1];
    const newHistory = current.history.slice(0, -1);
    setFileStates((prev) => ({
      ...prev,
      [activeFileId]: {
        ...prev[activeFileId],
        data: previous.data,
        headers: previous.headers,
        history: newHistory,
      },
    }));
    setStatusMsg("Undone");
  };

  const ActionButton = ({ id, label, onClick, disabled, className }) => (
    <button
      className={`cc-action-btn ${className || ""}`}
      onClick={onClick}
      disabled={disabled || !!processingAction}
    >
      {processingAction === id ? (
        <ClipLoader size={12} color="inherit" />
      ) : (
        label
      )}
    </button>
  );

  const currentFile = getCurrentState();
  const isJsonFile =
    filesList.find((f) => f.id === activeFileId)?.type === "json_mapping";

  return (
    <div className="cc-layout">
      {/* 1. HEADER */}
      <header className="cc-header">
        <div className="cc-header-right">
          <button
            className="cc-btn cc-btn-ghost"
            onClick={() => setShowSuggestions(!showSuggestions)}
            disabled={!currentFile || isJsonFile}
          >
            <FaMagic /> Hints ({suggestions.length})
          </button>
          <button
            className="cc-btn cc-btn-ghost"
            onClick={handleUndo}
            disabled={!currentFile?.history?.length || !!processingAction}
          >
            <FaUndo /> Undo
          </button>
          <button
            className="cc-btn cc-btn-cta"
            onClick={() => setShowSaveModal(true)}
            disabled={!filesList.length || !!processingAction}
          >
            <FaSave /> Save
          </button>
        </div>
      </header>

      {/* 2. SIDEBAR */}
      <aside className={`cc-sidebar ${isSidebarOpen ? "open" : ""}`}>
        {/* FILE EXPLORER SECTION */}
        <div className="cc-panel-section">
          <div className="cc-section-title">
            <FaFileCsv /> EXPLORER
          </div>
          <div className="cc-file-list">
            {filesList.map((f) => (
              <div
                key={f.id}
                className={`cc-file-item ${
                  activeFileId === f.id ? "active" : ""
                }`}
                onClick={() => {
                  if (!processingAction) {
                    setActiveFileId(f.id);
                    if (window.innerWidth < 768) setIsSidebarOpen(false);
                  }
                }}
              >
                {f.type === "json_mapping" ? <FaFileCode /> : <FaFileCsv />}
                <span>{f.name}</span>
                {fileStates[f.id]?.loading && (
                  <ClipLoader size={10} color="#58a6ff" />
                )}
              </div>
            ))}
          </div>
        </div>

        {/* TOOLS SECTION */}
        {!isJsonFile && (
          <div className="cc-panel-section" style={{ flex: 1 }}>
            <div className="cc-section-title">
              <FaColumns /> OPERATIONS
            </div>
            <div className="cc-tools-grid">
              {[
                { id: "structure", icon: <FaTable />, label: "Edit" },
                { id: "clean", icon: <FaBroom />, label: "Clean" },
                { id: "transform", icon: <FaCalculator />, label: "Math" },
                { id: "filter", icon: <FaFilter />, label: "Filter" },
                { id: "merge", icon: <FaLayerGroup />, label: "Join" },
              ].map((t) => (
                <div
                  key={t.id}
                  className={`cc-tool-card ${
                    activeTool === t.id ? "active" : ""
                  }`}
                  onClick={() => !processingAction && setActiveTool(t.id)}
                >
                  {t.icon}
                  <span>{t.label}</span>
                </div>
              ))}
            </div>

            {/* DYNAMIC TOOL PANEL */}
            {activeFileId &&
            (!fileStates[activeFileId] || fileStates[activeFileId].loading) ? (
              <div className="cc-empty-state">
                <ClipLoader color="#58a6ff" />
              </div>
            ) : (
              <>
                {/* 1. MERGE PANEL */}
                {activeTool === "merge" && (
                  <div className="cc-form-group">
                    <label>
                      <FaPlus /> Source B
                    </label>
                    <select
                      className="cc-input-control"
                      value={mergeConfig.targetFileId}
                      onChange={(e) =>
                        setMergeConfig({
                          ...mergeConfig,
                          targetFileId: e.target.value,
                        })
                      }
                    >
                      <option value="">Select File...</option>
                      {filesList
                        .filter(
                          (f) =>
                            f.id !== activeFileId && f.type !== "json_mapping"
                        )
                        .map((f) => (
                          <option key={f.id} value={f.id}>
                            {f.name}
                          </option>
                        ))}
                    </select>
                    {mergeConfig.targetFileId && (
                      <>
                        <div style={{ marginTop: "10px" }}>
                          <label>Keys (Left = Right)</label>
                          <div className="cc-flex-row">
                            <select
                              className="cc-input-control"
                              value={mergeConfig.primaryKey}
                              onChange={(e) =>
                                setMergeConfig({
                                  ...mergeConfig,
                                  primaryKey: e.target.value,
                                })
                              }
                            >
                              <option>Key A</option>
                              {currentFile?.headers?.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                            <select
                              className="cc-input-control"
                              value={mergeConfig.secondaryKey}
                              onChange={(e) =>
                                setMergeConfig({
                                  ...mergeConfig,
                                  secondaryKey: e.target.value,
                                })
                              }
                            >
                              <option>Key B</option>
                              {fileStates[
                                mergeConfig.targetFileId
                              ]?.headers?.map((h) => (
                                <option key={h} value={h}>
                                  {h}
                                </option>
                              ))}
                            </select>
                          </div>
                        </div>
                        <label>Join Type</label>
                        <div className="cc-flex-row">
                          {["left", "inner", "outer"].map((type) => (
                            <button
                              key={type}
                              className="cc-btn cc-btn-ghost"
                              style={{
                                border:
                                  mergeConfig.joinType === type
                                    ? "1px solid #58a6ff"
                                    : "1px solid #30363d",
                              }}
                              onClick={() =>
                                setMergeConfig({
                                  ...mergeConfig,
                                  joinType: type,
                                })
                              }
                            >
                              {type}
                            </button>
                          ))}
                        </div>
                        <ActionButton
                          id="merge"
                          label="Execute Join"
                          className="cc-btn-primary-action"
                          onClick={performMerge}
                        />
                      </>
                    )}
                  </div>
                )}

                {/* 2. STANDARD TOOLS */}
                {activeTool !== "merge" && (
                  <>
                    <div className="cc-form-group">
                      <label>Active Column</label>
                      <select
                        className="cc-input-control"
                        value={selectedCol}
                        onChange={(e) => setSelectedCol(e.target.value)}
                        disabled={!!processingAction}
                      >
                        <option value="">-- Select --</option>
                        {currentFile?.headers?.map((h) => (
                          <option key={h} value={h}>
                            {h}
                          </option>
                        ))}
                      </select>
                      {selectedCol && (
                        <span className="cc-badge-type">
                          {getColType.toUpperCase()}
                        </span>
                      )}
                    </div>

                    {activeTool === "structure" && (
                      <>
                        <div className="cc-form-group">
                          <div className="cc-flex-row">
                            <input
                              className="cc-input-control"
                              placeholder="New Name"
                              value={renameValue}
                              onChange={(e) => setRenameValue(e.target.value)}
                            />
                            <button
                              className="cc-btn cc-btn-ghost"
                              onClick={handleRenameColumn}
                            >
                              Rename
                            </button>
                          </div>
                          <ActionButton
                            id="convert_number"
                            label="Convert to Number"
                            onClick={() => handleTypeConversion("number")}
                          />
                          <ActionButton
                            id="convert_string"
                            label="Convert to String"
                            onClick={() => handleTypeConversion("string")}
                          />
                          <div style={{ marginTop: "20px" }}>
                            <ActionButton
                              id="drop_col"
                              label="Delete Column"
                              className="cc-btn-danger"
                              onClick={() => handleDropColumn()}
                            />
                          </div>
                        </div>
                      </>
                    )}

                    {activeTool === "clean" && (
                      <>
                        <ActionButton
                          id="remove_dupe"
                          label="Remove Duplicates"
                          onClick={handleRemoveDuplicates}
                        />
                        {selectedCol && (
                          <div style={{ marginTop: "10px" }}>
                            <label>Transformations</label>
                            <div className="cc-flex-row">
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => handleTextCleaning("trim")}
                              >
                                Trim
                              </button>
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => handleTextCleaning("upper")}
                              >
                                UPPER
                              </button>
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => handleTextCleaning("lower")}
                              >
                                lower
                              </button>
                            </div>
                            <label>Impute Missing</label>
                            <div className="cc-flex-row">
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => fillMissing("mean")}
                              >
                                Mean
                              </button>
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => fillMissing("mode")}
                              >
                                Mode
                              </button>
                              <button
                                className="cc-btn cc-btn-ghost"
                                style={{ color: "#f85149" }}
                                onClick={() => fillMissing("drop")}
                              >
                                Drop
                              </button>
                            </div>
                          </div>
                        )}
                      </>
                    )}

                    {activeTool === "transform" && selectedCol && (
                      <>
                        {getColType === "number" && (
                          <>
                            <label>Normalization</label>
                            <div className="cc-flex-row">
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => handleNormalization("minmax")}
                              >
                                MinMax
                              </button>
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => handleNormalization("zscore")}
                              >
                                Z-Score
                              </button>
                              <button
                                className="cc-btn cc-btn-ghost"
                                onClick={() => handleNormalization("log")}
                              >
                                Log
                              </button>
                            </div>
                          </>
                        )}
                        <label>Encoding</label>
                        <ActionButton
                          id="label_enc"
                          label="Label Encode"
                          onClick={() => labelEncode()}
                        />
                        <ActionButton
                          id="one_hot"
                          label="One-Hot Encode"
                          onClick={handleOneHotEncode}
                        />
                      </>
                    )}
                  </>
                )}
              </>
            )}
          </div>
        )}
      </aside>

      {/* 3. MAIN WORKSPACE */}
      <main className="cc-workspace">
        {showSuggestions && (
          <div className="cc-ai-panel cc-fade-in">
            <div className="cc-ai-header">
              <strong>Suggested Actions</strong>
              <FaTimes
                style={{ cursor: "pointer" }}
                onClick={() => setShowSuggestions(false)}
              />
            </div>
            <div className="cc-ai-content">
              {suggestions.length === 0 ? (
                <div className="cc-empty-state">No issues detected.</div>
              ) : (
                suggestions.map((s, i) => (
                  <div key={i} className="cc-ai-card">
                    <h5>{s.title}</h5>
                    <p>{s.desc}</p>
                    <button
                      className="cc-btn-primary-action"
                      style={{ fontSize: "10px", padding: "4px 8px" }}
                      onClick={s.action}
                    >
                      {s.actionLabel}
                    </button>
                  </div>
                ))
              )}
            </div>
          </div>
        )}

        {!currentFile ? (
          <div className="cc-empty-state">
            <FaFileCsv size={40} />
            <p>No File Selected</p>
          </div>
        ) : currentFile.loading ? (
          <div className="cc-empty-state">
            <ClipLoader color="#58a6ff" />
          </div>
        ) : isJsonFile ? (
          <div className="cc-json-pre">
            {JSON.stringify(currentFile.data, null, 2)}
          </div>
        ) : (
          <div className="cc-table-container">
            <table className="cc-table">
              <thead>
                <tr>
                  <th className="cc-sticky-index">#</th>
                  {currentFile.headers?.map((h) => (
                    <th
                      key={h}
                      className={selectedCol === h ? "active-col" : ""}
                      onClick={() => setSelectedCol(h)}
                    >
                      {h}
                    </th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {currentFile.data?.slice(0, 100).map((row, i) => (
                  <tr key={i}>
                    <td className="cc-sticky-index">{i + 1}</td>
                    {currentFile.headers?.map((h, j) => (
                      <td
                        key={j}
                        className={selectedCol === h ? "active-cell" : ""}
                      >
                        {row[h] === null || row[h] === "" ? (
                          <span
                            style={{ color: "#f85149", fontStyle: "italic" }}
                          >
                            NaN
                          </span>
                        ) : (
                          String(row[h])
                        )}
                      </td>
                    ))}
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </main>

      {/* MODAL */}
      {showSaveModal && (
        <div className="cc-modal-overlay">
          <div className="cc-modal">
            <h3>Save Checkpoint</h3>
            <div className="cc-modal-list">
              {filesList.map((f) => (
                <div key={f.id} className="cc-modal-item">
                  <input
                    type="checkbox"
                    checked={filesToSave[f.id]?.selected}
                    onChange={() =>
                      !filesToSave[f.id]?.isMandatory &&
                      setFilesToSave((prev) => ({
                        ...prev,
                        [f.id]: {
                          ...prev[f.id],
                          selected: !prev[f.id].selected,
                        },
                      }))
                    }
                    disabled={filesToSave[f.id]?.isMandatory || isSaving}
                  />
                  <span style={{ flex: 1, color: "var(--text-main)" }}>
                    {f.name}
                  </span>
                  <span className="cc-badge-type">
                    {f.type === "json_mapping" ? "JSON" : "CSV"}
                  </span>
                </div>
              ))}
            </div>
            {isSaving && (
              <div
                style={{
                  height: "2px",
                  background: "#21262d",
                  width: "100%",
                  marginBottom: "10px",
                }}
              >
                <div
                  style={{
                    height: "100%",
                    width: `${uploadProgress}%`,
                    background: "#58a6ff",
                  }}
                ></div>
              </div>
            )}
            <div
              style={{
                display: "flex",
                justifyContent: "flex-end",
                gap: "10px",
              }}
            >
              <button
                className="cc-btn cc-btn-ghost"
                onClick={() => setShowSaveModal(false)}
              >
                Cancel
              </button>
              <button
                className="cc-btn cc-btn-primary-action"
                onClick={handleSaveInstance}
              >
                {isSaving ? "Saving..." : "Confirm Save"}
              </button>
            </div>
          </div>
        </div>
      )}
      {/* 4. FOOTER STATUS BAR */}
      <footer className="cc-footer">
        <div className="cc-footer-group">
          <div className="cc-footer-item">
            <FaFileCsv /> {currentFile?.name || "No File"}
          </div>
          {statusMsg && (
            <div
              className="cc-footer-item"
              style={{ color: "#fff", fontWeight: "bold" }}
            >
              {statusMsg}
            </div>
          )}
        </div>
        <div className="cc-footer-group">
          <div className="cc-footer-item">
            Rows: {currentFile?.metrics?.totalRows || 0}
          </div>
          <div className="cc-footer-item">
            Cols: {currentFile?.metrics?.totalColumns || 0}
          </div>
        </div>
      </footer>
    </div>
  );
};

export default CSVCanvas;
